import React, { useState, useCallback } from 'react';
import styles from './StarRating.module.scss';

interface StarRatingProps {
  value: number;
  maxStars: number;
  onChange: (rating: number) => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showValue?: boolean;
  showTooltip?: boolean;
}

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  maxStars,
  onChange,
  disabled = false,
  size = 'medium',
  showValue = true, // Changed default to true
  showTooltip = true
}) => {
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [focusedStar, setFocusedStar] = useState<number>(-1);

  const handleStarClick = useCallback((starIndex: number) => {
    if (!disabled) {
      const newRating = starIndex + 1;
      onChange(newRating);
      
      // Add a small delay to show the selection effect
      setTimeout(() => {
        setHoverRating(0);
      }, 150);
    }
  }, [disabled, onChange]);

  const handleStarHover = useCallback((starIndex: number) => {
    if (!disabled) {
      setHoverRating(starIndex + 1);
    }
  }, [disabled]);

  const handleMouseLeave = useCallback(() => {
    if (!disabled) {
      setHoverRating(0);
    }
  }, [disabled]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, starIndex: number) => {
    if (disabled) return;
    
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleStarClick(starIndex);
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (starIndex < maxStars - 1) {
          setFocusedStar(starIndex + 1);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (starIndex > 0) {
          setFocusedStar(starIndex - 1);
        }
        break;
    }
  }, [disabled, maxStars, handleStarClick]);

  const renderStar = (index: number) => {
    const starValue = index + 1;
    const isFilled = hoverRating ? starValue <= hoverRating : starValue <= value;
    const isHalf = !isFilled && starValue === Math.ceil(value) && value % 1 !== 0;
    const isFocused = focusedStar === index;

    const getTooltipText = () => {
      const ratings = [
        'Sehr schlecht',
        'Schlecht', 
        'Okay',
        'Gut',
        'Sehr gut',
        'Ausgezeichnet',
        'Hervorragend',
        'Perfekt',
        'Fantastisch',
        'Überragend'
      ];
      return ratings[starValue - 1] || `${starValue} Sterne`;
    };

    return (
      <span
        key={index}
        className={`${styles.star} ${styles[size]} ${isFilled ? styles.filled : ''} ${isHalf ? styles.half : ''} ${isFocused ? styles.focused : ''}`}
        onClick={() => handleStarClick(index)}
        onMouseEnter={() => handleStarHover(index)}
        onMouseLeave={handleMouseLeave}
        onKeyDown={(e) => handleKeyDown(e, index)}
        onFocus={() => setFocusedStar(index)}
        onBlur={() => setFocusedStar(-1)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`${starValue} von ${maxStars} Sternen`}
        title={showTooltip ? getTooltipText() : undefined}
        data-rating={starValue}
      >
        ★
      </span>
    );
  };

  const getRatingText = () => {
    if (value === 0) return 'Keine Bewertung';
    if (value === 1) return '1 Stern';
    return `${value} Sterne`;
  };

  return (
    <div className={styles.starRatingContainer}>
      <div 
        className={styles.starsWrapper}
        onMouseLeave={handleMouseLeave}
      >
        {Array.from({ length: maxStars }, (_, index) => renderStar(index))}
      </div>
    </div>
  );
};

export default StarRating; 